/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/shipment/list/filters.tsx — its
 * `FilterElements`: `DateFiled`, inline on a wide toolbar with the label hidden, full-width and
 * labelled inside the drawer. Two separate `DateFiled`s (from/to), not one `selectsRange` field —
 * each writes its own search param and bounds the other (`maxDate`/`minDate`).
 *
 * Adapted for server-side filtering: every control writes the route's search params (`from`, `to`,
 * `customerId`) rather than a TanStack column filter, because the loader re-fetches the page on
 * every navigation. `PriceField` stays left out — no amount filter on `GET /admin/orders`.
 *
 * State is the status dropdown and the order-number search is the toolbar's own, both in List.tsx.
 * That search is a route param the loader re-fetches on, deliberately NOT `table.setGlobalFilter`:
 * `useTanStackTable`'s `data` is only the current server page (`PagedTable` re-seeds it from
 * `result.items` on every navigation), so a global filter would silently search one page while
 * looking like it searches every order. `integrations/tally` uses `setGlobalFilter` for exactly the
 * opposite reason — its table is not server-paged.
 */
import { useNavigate } from "react-router-dom";
import { PiXBold } from "react-icons/pi";
import { Button } from "rizzui";
import { FILTER_LABEL } from "../../components/table/toolbar";

import { DateField } from "../../components/date-field";
import { parseIsoDate, toIsoDate } from "../../lib/format";

export type OrderFilterValues = { orderNumber?: string; state?: string; from?: string; to?: string; customerId?: string };


export function Filters({
  value,
  customerName,
  query,
}: {
  value: Pick<OrderFilterValues, "from" | "to" | "customerId">;
  /** The filtered customer's server-sent name; never the id. */
  customerName?: string | null;
  query: (over: Record<string, string | undefined>) => string;
}) {
  const navigate = useNavigate();
  const { from, to, customerId } = value;

  // Every control resets the page — a filter change means a different result set.
  const apply = (over: OrderFilterValues) => navigate(query({ page: undefined, ...over }));

  const fromDate = parseIsoDate(from ?? null);
  const toDate = parseIsoDate(to ?? null);

  return (
    <>
      {/* Two independent pickers, not one range field — each writes its own `from`/`to` param, and
          each bounds the other (maxDate/minDate) so a reader can't pick an inverted range.
          `suffix: null` drops `DateFiled`'s default dropdown-caret icon — the calendar icon
          (prefix) is the only "open the calendar" affordance. Clearing is rizzui's own
          `clearable`/`onClear` (the same plain "×" the order-number search box next to it uses),
          not react-datepicker's `isClearable` — that draws a hard-coded blue circle, the one
          clear style in this app that doesn't match rizzui's. */}
      <DateField
        dateFormat="dd-MMM-yyyy"
        className="w-full @5xl:w-48"
        placeholderText="Placed from…"
        selected={fromDate}
        maxDate={toDate ?? undefined}
        onChange={(date) => apply({ from: date ? toIsoDate(date as Date) : undefined })}
        inputProps={{
          label: "Placed from",
          labelClassName: FILTER_LABEL,
          suffix: null,
          clearable: Boolean(fromDate),
          onClear: () => apply({ from: undefined }),
        }}
      />
      <DateField
        dateFormat="dd-MMM-yyyy"
        className="w-full @5xl:w-48"
        placeholderText="Placed to…"
        selected={toDate}
        minDate={fromDate ?? undefined}
        onChange={(date) => apply({ to: date ? toIsoDate(date as Date) : undefined })}
        inputProps={{
          label: "Placed to",
          labelClassName: FILTER_LABEL,
          suffix: null,
          clearable: Boolean(toDate),
          onClear: () => apply({ to: undefined }),
        }}
      />
      {/* Only ever set by a link (a customer's "Orders from this customer"), so it shows as the
          filter it is, with its own way out, rather than as an empty field. */}
      {customerId ? (
        <Button
          size="sm"
          variant="flat"
          className="h-9"
          aria-label={`Customer ${customerName ?? "Unknown customer"}, clear this filter`}
          onClick={() => apply({ customerId: undefined })}
        >
          Customer <span className="ms-1">{customerName ?? "Unknown customer"}</span>
          <PiXBold aria-hidden className="ms-2 size-3.5" />
        </Button>
      ) : null}
    </>
  );
}
